import React from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import Add from '@mui/icons-material/Add'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { IGetRevenueGroupsItem } from '@cbgms/api'
import { RevenueGroupKind, VehicleSalesType, WorkorderLineType } from '@cbgms/api/modules/enums'
import { useNotifier } from '@cbgms/base/notifier'
import { IQuery } from '@cbgms/components/list/query'
import { IFields } from '@cbgms/components/list/fields'
import { SubmitButton } from '@carsys/form'
import { createRevenueGroup, getRevenueGroupAdministrationsLedgers } from 'app/settings/state/RevenueGroups/actions'
import { loadRevenueGroups } from 'app/settings/state/Ledgers/actions'
import { MainRevenueGroupForm, RevenueGroupAdministrationFormValues } from './MainRevenueGroupForm'
import { generateSubmissionError } from 'utils/Form/SubmissionError'
import { Redirect } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Dialog, ViewLoader } from '@carsys/ui'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { IRevenueGroupAdministration } from '@cbgms/api/modules/revenuegroups'
import { FallbackForDepartments } from './FallbackForDepartments'
import { loadAllDepartments } from 'app/settings/state/Departments/actions'
import AdministrationTabs from './AdministrationTabs'
import Departments from './Departments'
import { generateAdministrationWithMissingLedgers } from './utils'
import InvoiceLedgers from './InvoiceLedgers'

interface WorkshopReadOnlyInitialValues {
  Kind: RevenueGroupKind
  ProductType: WorkorderLineType
  IsDefaultForProductType?: boolean
}

interface VehicleSalesReadOnlyInitialValues {
  Kind: RevenueGroupKind
  VehicleSalesType: VehicleSalesType
  IsDefaultForProductType?: boolean
}

type ReadOnlyInitialValues = WorkshopReadOnlyInitialValues | VehicleSalesReadOnlyInitialValues

interface IAddNewRevenueDialog {
  vehicleUUID?: string
  search?: IQuery<IGetRevenueGroupsItem>
  fields?: IFields<IGetRevenueGroupsItem>
  addRevenueFunc?: () => any
  disabled?: boolean
  /**
   * Read only initial values should be provided when you want to
   * create a revenue group in a certain context. For example when
   * creating a revenue group in the stock view.
   */
  readOnlyInitialValues?: ReadOnlyInitialValues
}

const AddNewRevenueDialog: React.FC<IAddNewRevenueDialog> = props => {
  const { vehicleUUID, search, fields, addRevenueFunc, disabled, readOnlyInitialValues } = props
  const [openDialog, setOpenDialog] = React.useState(false)
  const { t } = useTranslation()
  const notifier = useNotifier()
  const dispatch = useDispatch()
  const [tabIndex, setTabIndex] = React.useState<number>(0)

  const changeTabIndex = (index: number) => setTabIndex(index)

  const onAddRevenueGroup = (_event?: React.ChangeEvent<any>) => {
    setOpenDialog(true)
  }

  const onSubmitRevenueGroupSuccess = async () => {
    if (search && fields) {
      await dispatch(
        loadRevenueGroups({
          fields: fields.toString(),
          perPage: 200,
          search: search.toString()
        })
      )
    } else {
      await dispatch(loadRevenueGroups({ fields: 'RevenueGroup.*' }))
    }
    setOpenDialog(false)

    notifier.success(t('revenue_group.http_success_created'))
    addRevenueFunc?.()
  }

  const onSubmitRevenueGroup = async (values: RevenueGroupAdministrationFormValues) => {
    try {
      const createValues = { ...values }
      if (createValues.Kind === RevenueGroupKind.Workshop) {
        createValues.VehicleSalesType = ''
      } else {
        createValues.ProductType = ''
      }

      const { Data } = await dispatch(createRevenueGroup(createValues))
      await onSubmitRevenueGroupSuccess()
      if (vehicleUUID) {
        return `/rental-vehicles/vehicles/${vehicleUUID}#replacementVehicle`
      } else {
        return { ...values, UUID: Data.UUID }
      }
    } catch (error) {
      throw generateSubmissionError(error)
    }
  }

  const revenueGroupAdministrationsLedgers = useQuery(['RevenueGroup', 'New'], async () => {
    const { Data } = await dispatch(getRevenueGroupAdministrationsLedgers(EmptyUUID))
    const departments = await dispatch(loadAllDepartments())
    const structuredDepartments = departments.Data.map(department => ({
      uuid: department.Department.UUID,
      name: department.Department.Name
    }))

    const revenueGroup: IRevenueGroupAdministration = {
      UUID: Data.UUID,
      Name: Data.Name,
      Kind: Data.Kind,
      VatLimiter: Data.VatLimiter,
      HideInGeneralSelection: Data.HideInGeneralSelection,
      ProductType: Data.ProductType,
      VehicleSalesType: Data.VehicleSalesType,
      IsDefaultForProductType: Data.IsDefaultForProductType,
      VatWhenUsedAsDefault: Data.VatWhenUsedAsDefault,
      CreatedAt: Data.CreatedAt,
      UpdatedAt: Data.UpdatedAt,
      Administrations: Data.Administrations.map(Administration =>
        generateAdministrationWithMissingLedgers(Administration, Data.VatLimiter, structuredDepartments)
      )
    }

    return { revenueGroup, departments: structuredDepartments }
  })

  if (revenueGroupAdministrationsLedgers.isLoading) {
    return <ViewLoader />
  }

  if (revenueGroupAdministrationsLedgers.isError || !revenueGroupAdministrationsLedgers.data) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/settings/revenue-groups' />
  }

  const requiredMessage = t('global.field_is_required')
  const incorrectTypeMessage = `${t('global.must_be_a_number')}. `

  const validationSchema = Yup.object().shape({
    Name: Yup.string().required(requiredMessage).min(1, requiredMessage),
    Kind: Yup.string().required(requiredMessage),
    VehicleSalesType: Yup.string().when('Kind', {
      is: (value: string) => value && value === RevenueGroupKind.CarSales,
      then: schema => schema.required(requiredMessage)
    }),
    ProductType: Yup.string().when('Kind', {
      is: (value: string) => value && value === RevenueGroupKind.Workshop,
      then: schema => schema.required(requiredMessage)
    }),
    VatLimiter: Yup.array().required(requiredMessage).min(1, requiredMessage),
    VatWhenUsedAsDefault: Yup.string().required(requiredMessage).min(1, requiredMessage),
    Administrations: Yup.array().when('AdministrationUUID', {
      is: (value: string) => value !== EmptyUUID,
      then: schema =>
        schema.of(
          Yup.object({
            Ledgers: Yup.array().when('DepartmentUUID', {
              is: (value: string) => value === '',
              then: schema =>
                schema.of(
                  Yup.object({
                    Revenue: Yup.number().typeError(incorrectTypeMessage),
                    Discount: Yup.number().typeError(incorrectTypeMessage)
                  })
                ),
              otherwise: schema =>
                schema.of(
                  Yup.object({
                    Revenue: Yup.number().when('IsRevenueFieldRequired', {
                      is: (value: boolean) => value === true,
                      then: schema => schema.required(requiredMessage).typeError(incorrectTypeMessage),
                      otherwise: schema => schema.typeError(incorrectTypeMessage)
                    }),
                    Discount: Yup.number().when('IsDiscountFieldRequired', {
                      is: (value: boolean) => value === true,
                      then: schema => schema.required(requiredMessage).typeError(incorrectTypeMessage),
                      otherwise: schema => schema.typeError(incorrectTypeMessage)
                    })
                  })
                )
            })
          })
        )
    })
  })

  return (
    <>
      <Button
        onClick={onAddRevenueGroup}
        style={{ height: '40px', width: '40px', borderBottomLeftRadius: 0, borderTopLeftRadius: 0, borderLeft: 'none' }}
        disabled={disabled}
        id='revenue-plus-button'
        variant='outlined'
      >
        <Add />
      </Button>
      {openDialog && (
        <Dialog disableEnforceFocus fullWidth maxWidth='md' open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>{t('revenue_group.add_new')}</DialogTitle>
          <Formik
            enableReinitialize
            initialValues={{ ...revenueGroupAdministrationsLedgers.data.revenueGroup, ...readOnlyInitialValues }}
            onSubmit={onSubmitRevenueGroup}
            validationSchema={validationSchema}
          >
            {({ errors, submitCount }) => {
              const administrationErrors = submitCount > 0 ? errors?.Administrations : undefined

              return (
                <Form>
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MainRevenueGroupForm
                          hasReadOnlyInitialValues={Boolean(readOnlyInitialValues)}
                          departments={revenueGroupAdministrationsLedgers.data.departments}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AdministrationTabs
                          tabErrors={administrationErrors as any[] | undefined}
                          tabIndex={tabIndex}
                          changeTabIndex={changeTabIndex}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FallbackForDepartments tabIndex={tabIndex} />
                      </Grid>
                      <Grid item xs={12}>
                        <Departments tabIndex={tabIndex} departments={revenueGroupAdministrationsLedgers.data.departments} />
                      </Grid>
                      <Grid item xs={12}>
                        <InvoiceLedgers tabIndex={tabIndex} />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t('global.cancel')}</Button>
                    <SubmitButton />
                  </DialogActions>
                </Form>
              )
            }}
          </Formik>
        </Dialog>
      )}
    </>
  )
}

AddNewRevenueDialog.displayName = 'AddNewRevenueDialog'

export default AddNewRevenueDialog
